import {
	Alert,
	Badge,
	Box,
	Checkbox,
	CircularProgress,
	FormControl,
	FormControlLabel,
	FormLabel,
	IconButton,
	Menu,
	Radio,
	RadioGroup,
	Tab,
	Tabs,
	ToggleButton,
	ToggleButtonGroup,
} from "@mui/material";
import { useEffect, useState } from "react";
import { PdfTemplateSelect } from "./pdf-template-select.component";
import { clientExample } from "../../constants/clientExample";
import { jobExample } from "../../constants/jobExample";
import { IconChevronLeft, IconChevronRight } from "@tabler/icons-react";
import { drawDOM, exportPDF } from "@progress/kendo-drawing";
import { Document, Page } from "react-pdf";
import { jobDocumentExample } from "../../constants/jobDocumentExample";
import { usePdfTemplateConfigurationContext } from "../../context/PdfTemplateConfigurationContext";
import { patientExample } from "../../constants/patientExample";
import { useSearchParams } from "react-router-dom";
import {
	PdfTemplateSingleConfigurations,
	TitleDateModeSettings,
} from "./pdf-template-single-configurations.component";
import { PdfTemplateType } from "../../pdf-templates/document-template.types";
import {
	PaymentSlipDataInput,
	formatPaymentSlipData,
} from "../../pdf-templates/pdf-template-components/qr-payment-slip/qr-payment-helpers";
import { BankAccountSelect } from "../../forms/form-components/bank-account/input-components/bank-account.select";
import {
	BankAccountEntityType,
	ClientEntityType,
} from "@/lib/supabase/supabaseTypes";
import { bankAccountExample } from "../../constants/bankAccountExample";
import { ClientSelect } from "../../forms/form-input-components/selects/client.select";
import DownloadIcon from "@mui/icons-material/Download";
import CachedIcon from "@mui/icons-material/Cached";
import {
	ExtendedJobDocument,
	calculateJobTotalsLegacy,
	calculateSammelrechnungRowsAndTotals,
} from "../../lib/utils/calculate";
import { CustomTextFieldWithSaveButton } from "../../forms/form-input-components/custom-input-components/custom-text-field-with-save-button.component";
import { PdfTemplateOptions } from "./pdf-selected-template.config";
import { resolveCurrencyConverterValues } from "../../hooks/actions/useExportActions";
import { jobItemExamples } from "../../constants/jobItemExample";
import { useCentralStore } from "../../store/Central";
import { PdfTemplateLogo } from "./pdf-template-logo.component";
import { Logger } from "@/lib/logger/Logger";
import { useStorage } from "../../hooks/useStorage";
import { supabase } from "@/lib/supabase";
import { StorageBucketsEnum } from "../../types/enums";
import { Alignment } from "../../pdf-templates/pdf-templates/types";
import { PDFTemplateConfigurationSettings } from "./pdf-template-configuration-settings.component";
import FeedbackIcon from "@mui/icons-material/Feedback";
import {
	Accordion,
	AccordionItem,
	AccordionTrigger,
	AccordionContent,
} from "../../components/ui/accordion";
import { JobDocumentTypeEnum } from "../job-page/job-document/job-document.types";
import { practitionerExample } from "../../constants/practitionerExample";
import { createCustomFooterTemplate } from "../../pdf-templates/pdf-template-components/pdf-custom-footer-template.component";
import { ColorPicker } from "../../components/ui/color-picker.component";
import { base64ToBlob } from "../../lib/utils/utils-functions";
import { uploadFileWithPublicUrl } from "../../hooks/useStorage";
import { v4 as uuidv4 } from "uuid";
import { showNotification } from "../../store/Central/selectors";
import { renderPdfToCanvases } from "../support/pdf-diff/utils/render-pdf";
import { ReminderLetterSettings } from "./reminder-letter-settings.component";
import { isReminderTemplateType } from "../debitoren/mahnwesen/utils/reminder-stage";

export const PdfTemplateConfiguration: React.FC = () => {
	// Persist the selected template via ?template=...
	const [searchParams, setSearchParams] = useSearchParams();
	const { fileUrl, downloadBlob } = useStorage();
	const template = searchParams.get("template");

	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const open = Boolean(anchorEl);
	const handleClick = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(event.currentTarget);
	};
	const handleClose = () => {
		setAnchorEl(null);
	};

	const [selectedBankAccount, setSelectedBankAccount] =
		useState<BankAccountEntityType | null>(null);
	const bankAccount = selectedBankAccount ?? bankAccountExample;

	const {
		clientsLookup,
		clients,
		organization,
		bankAccountsLookup,
		activatedRegions,
	} = useCentralStore((state) => ({
		clientsLookup: state.clientsLookup,
		clients: Object.values(state.clientsLookup),
		organization: state.organization,
		bankAccountsLookup: state.bankAccountsLookup,
		activatedRegions: state.activatedRegions,
	}));
	const [selectedClient, setSelectedClient] =
		useState<ClientEntityType | null>(null);
	const client = selectedClient ?? clientExample;

	useEffect(() => {
		if (Object.keys(bankAccountsLookup).length > 0) {
			setSelectedBankAccount(
				bankAccountsLookup[Object.keys(bankAccountsLookup)[0]]
			);
		}
	}, [bankAccountsLookup]);

	useEffect(() => {
		if (clients.length > 0) {
			setSelectedClient(clients[0]);
		}
	}, []);

	const {
		pdfTemplateConfiguration,
		updatePdfTemplateConfiguration,
		resetConfigurationColumn,
	} = usePdfTemplateConfigurationContext();

	const [selectedPdfTemplate, setSelectedPdfTemplate] =
		useState<PdfTemplateType>(
			(template as PdfTemplateType) || PdfTemplateType.KOSTENVORANSCHLAG
		);
	const [pageNumber, setPageNumber] = useState<number>(1);
	const [numPages, setNumPages] = useState<number>(0);
	const [pdfBase64String, setPdfBase64String] = useState<string>("");
	const [isDocumentLoading, setIsDocumentLoading] = useState(true);
	const [isPageLoading, setIsPageLoading] = useState(false);
	const [clientAvatarUrl, setClientAvatarUrl] = useState<string | null>(null);
	const [currentTab, setCurrentTab] = useState(1);
	const [previewKey, setPreviewKey] = useState(0);
	const [previewCurrencyValues, setPreviewCurrencyValues] = useState<
		Record<string, string>
	>({});

	useEffect(() => {
		const fetchCustomImage = async () => {
			if (!pdfTemplateConfiguration?.general.customIconPath) return;
			const { data } = await supabase.storage
				.from(StorageBucketsEnum.ORG_V1)
				.createSignedUrl(
					pdfTemplateConfiguration?.general.customIconPath ?? "",
					3600,
					{}
				);

			if (data && !pdfTemplateConfiguration?.general.customImgUrl) {
				updatePdfTemplateConfiguration("general", {
					customImgUrl: data.signedUrl,
				});
				// Clear the url when it expires
				setTimeout(() => {
					updatePdfTemplateConfiguration("general", {
						customImgUrl: "",
					});
				}, 3599 * 1000);
			}
		};
		fetchCustomImage();
	}, [pdfTemplateConfiguration?.general]);

	useEffect(() => {
		refreshPreviewPage();
	}, [
		selectedPdfTemplate,
		pdfTemplateConfiguration,
		bankAccount,
		clientAvatarUrl,
	]);

	useEffect(() => {
		setIsPageLoading(true);
		setPageNumber(1);
	}, [selectedPdfTemplate]);

	useEffect(() => {
		const fetchClientAvatarUrl = async () => {
			let clientAvatarPath = client?.avatar_path;
			if (!clientAvatarPath) {
				setClientAvatarUrl(null);
				refreshPreviewPage();
				return;
			}

			//TODO(Eyob): Why is the client.avatar_path already signed?
			// https://dentdesk.atlassian.net/browse/DW-884
			if (clientAvatarPath.startsWith("http")) {
				clientAvatarPath = clientAvatarPath
					?.split("supabase.co/storage/v1/render/image/sign/v1/")[1]
					?.split("?")[0];
			}

			const imageSrc = await fileUrl(clientAvatarPath);

			Logger.info("clientAvatarPath", clientAvatarPath, imageSrc);

			if (!imageSrc.data) {
				setClientAvatarUrl(null);
				refreshPreviewPage();
				return;
			}

			setClientAvatarUrl(imageSrc.data.signedUrl);
		};

		fetchClientAvatarUrl();
	}, [client]);

	// Make sure the Context is initialized
	if (!pdfTemplateConfiguration || !updatePdfTemplateConfiguration) {
		return (
			<div className="flex items-center justify-center p-8">
				<CircularProgress />
			</div>
		);
	}

	const handleSelectPdfTemplate = (value: PdfTemplateType) => {
		setIsDocumentLoading(true);
		setSelectedPdfTemplate(value);
		setSearchParams({ ...searchParams, template: value });
	};

	const refreshPreviewPage = async () => {
		setIsDocumentLoading(true);
		// Resolve #WAEHRUNGSRECHNER rates BEFORE capturing — the FX fetch is async, but the
		// drawDOM snapshot below is synchronous, so the values must already be in the DOM.
		const currencyValues = await resolveCurrencyConverterValues(
			jobTotals.total.value,
			pdfTemplateConfiguration
		);
		setPreviewCurrencyValues(currencyValues);
		setPreviewKey((prev) => prev + 1);
		await new Promise((resolve) => setTimeout(resolve, 100));
		const pdfElement = document.querySelector(
			".pdf-preview"
		) as HTMLElement;

		// Prepare drawDOM options
		const drawOptions: any = {
			paperSize: ["210mm", "297mm"],
			keepTogether: "p",
			scale: 0.75,
		};

		if (
			pdfTemplateConfiguration?.general?.customFooterEnabled ||
			pdfTemplateConfiguration?.general?.backgroundImageEnabled
		) {
			drawOptions.template = createCustomFooterTemplate(
				pdfTemplateConfiguration,
				selectedPdfTemplate
			);
		}

		await drawDOM(pdfElement, drawOptions)
			.then(async (group) => {
				return await exportPDF(group, {});
			})
			.then((dataUri) => {
				const base64String = dataUri.split(";base64,")[1];
				setPdfBase64String(base64String);
			});
	};

	const paymentSlipData: PaymentSlipDataInput = {
		bankAccount: bankAccount,
		recipient: client,
		amount: "1234.56",
		currency: "CHF",
		invoiceNumber: "000001",
	};

	const errorsAlerts: {
		severity: "warning" | "error";
		message: string;
	}[] = [];

	if (!client) {
		errorsAlerts.push({
			severity: "warning",
			message: "Kein Beispiel-Auftraggeber ausgewählt",
		});
	}
	if (!bankAccount) {
		errorsAlerts.push({
			severity: "warning",
			message: "Kein Beispiel-Bankkonto ausgewählt",
		});
	}
	if (!organization?.name) {
		errorsAlerts.push({
			severity: "warning",
			message: "Firmenname des Labors fehlt in den Einstellungen",
		});
	}
	if (!organization?.street) {
		errorsAlerts.push({
			severity: "warning",
			message: "Strasse fehlt in der Laboradresse in den Einstellungen",
		});
	}
	if (!organization?.city) {
		errorsAlerts.push({
			severity: "warning",
			message: "Ort fehlt in der Laboradresse in den Einstellungen",
		});
	}
	if (
		activatedRegions.length > 0 &&
		activatedRegions.every((region) => !region.mwst_number)
	) {
		errorsAlerts.push({
			severity: "warning",
			message: "MwSt.-Nummer fehlt in den Einstellungen",
		});
	}
	if (
		activatedRegions.length > 0 &&
		activatedRegions.every((region) => !region.mwst_percentage)
	) {
		errorsAlerts.push({
			severity: "warning",
			message: "MwSt.-Satz fehlt in den Einstellungen",
		});
	}

	const { error: paymentSlipError } = formatPaymentSlipData(paymentSlipData);
	if (paymentSlipError) {
		errorsAlerts.push({ severity: "error", message: paymentSlipError });
	}

	if (!organization) {
		return <div>Lädt...</div>;
	}
	if (!client) {
		return <div>Lädt...</div>;
	}
	const jobTotals = calculateJobTotalsLegacy({
		job: jobExample,
		jobDocument: jobDocumentExample[selectedPdfTemplate],
		jobItems: jobItemExamples,
		organization,
		client,
	});
	const extendedJobDocumentsExample: ExtendedJobDocument[] = Array.from(
		{ length: 3 },
		(_, i) => i
	).map(() => {
		return {
			job_client_id: clientExample.id,
			job_code: jobExample.code,
			job_mwst_number: jobExample.mwst_number,
			job_document_date: jobDocumentExample[selectedPdfTemplate].date,
			job_document_discount_material:
				jobDocumentExample[selectedPdfTemplate].discount_material,
			job_document_discount_work:
				jobDocumentExample[selectedPdfTemplate].discount_work,
			job_document_id: jobDocumentExample[selectedPdfTemplate].id,
			job_items: jobItemExamples.map((item) => ({
				id: item.id,
				price: item.price,
				quantity: item.quantity,
				tp_value: item.tp_value,
				discount: item.discount,
				is_req_dr_tax: item.is_req_dr_tax || false,
				is_warranty: item.is_warranty || false,
				type: item.type,
				code: "112",
				code_e: "112",
				description: "description",
				note: "",
			})),
			job_title: jobExample.title,
			organization_id: organization.id,
			patient_code: patientExample.code,
			patient_first_name: patientExample.first_name,
			patient_last_name: patientExample.last_name,
			patient_title: patientExample.title,
			practitioner_id: practitionerExample.id,
			practitioner_first_name: practitionerExample.first_name,
			practitioner_last_name: practitionerExample.last_name,
			practitioner_title: practitionerExample.title,
			job_document_type: JobDocumentTypeEnum.DeliveryNote,
			is_ignore_monthly_discount: false,
			job_tax_rate: jobExample.tax_rate,
			patient_birth_date: patientExample.birth_date,
			job_document_amount: jobDocumentExample[selectedPdfTemplate].amount,
			job_document_code: jobDocumentExample[selectedPdfTemplate].code,
			job_document_created_at:
				jobDocumentExample[selectedPdfTemplate].created_at,
			job_document_created_by:
				jobDocumentExample[selectedPdfTemplate].created_by,
			job_document_deleted_at:
				jobDocumentExample[selectedPdfTemplate].deleted_at,
			job_document_fae_id: jobDocumentExample[selectedPdfTemplate].fae_id,
			job_document_job_id: jobDocumentExample[selectedPdfTemplate].job_id,
			job_document_work_title:
				jobDocumentExample[selectedPdfTemplate].work_title,
			job_document_modified_at:
				jobDocumentExample[selectedPdfTemplate].modified_at,
			job_document_modified_by:
				jobDocumentExample[selectedPdfTemplate].modified_by,
			job_document_note: jobDocumentExample[selectedPdfTemplate].note,
			job_document_status: jobDocumentExample[selectedPdfTemplate].status,
			job_document_title: jobDocumentExample[selectedPdfTemplate].title,
			job_document_type_data:
				jobDocumentExample[selectedPdfTemplate].type_data,
			job_currency_text: jobExample.currency_text,
			job_round_to: jobExample.round_to,
		} as ExtendedJobDocument;
	});

	const mwstPercentage = extendedJobDocumentsExample[0].job_tax_rate ?? 0;
	const sammelrechnungCalculationResult =
		calculateSammelrechnungRowsAndTotals(
			extendedJobDocumentsExample,
			organization,
			clientExample,
			clientsLookup,
			mwstPercentage
		);

	const goBackPage = () => {
		if (pageNumber > 1) {
			setIsPageLoading(true);
			setPageNumber((prev) => prev - 1);
		}
	};

	const goForwardPage = () => {
		if (pageNumber < numPages) setIsPageLoading(true);
		setPageNumber((prev) => Math.min(prev + 1, numPages));
	};

	const handlePageHasFinishedLoading = () => {
		setTimeout(() => {
			setIsPageLoading(false);
		}, 200);
	};

	const handleDocumentHasFinishedLoading = () => {
		setTimeout(() => {
			setIsDocumentLoading(false);
		}, 200);
	};

	return (
		<div className="flex flex-col">
			<div className="flex flex-row border border-gray-200 p-4 gap-2 items-center w-full">
				<PdfTemplateSelect
					value={selectedPdfTemplate}
					onChange={handleSelectPdfTemplate}
				/>
				<div>
					<IconButton
						onClick={async () => {
							const blob = base64ToBlob(
								pdfBase64String,
								"application/pdf"
							);
							await downloadBlob(blob, "Kostenvoranschlag.pdf");
						}}
					>
						<DownloadIcon />
					</IconButton>
					<IconButton onClick={refreshPreviewPage}>
						<CachedIcon />
					</IconButton>
				</div>
			</div>
			<div
				style={{
					display: "flex",
					flexDirection: "row",
					height: "100%",
					width: "100%",
					justifyContent: "center",
					gap: "2%",
				}}
			>
				<div
					style={{
						padding: "40px 0px 40px 0px",
						width: "40%",
						minWidth: "300px",
						maxWidth: "900px",
						display: "flex",
						flexDirection: "column",
						gap: "20px",
					}}
				>
					{/* PDF TEMPLATE SELECT */}
					{/* ACCORDION */}
					<div>
						<div className="w-full overflow-x-auto">
							<Tabs
								value={currentTab}
								onChange={(e, v) => {
									setCurrentTab(v);
								}}
								aria-label="basic tabs example"
								className="w-full"
							>
								<Tab label="Beispieldaten" />
								<Tab label="Allgemein" />
								<Tab label="Aktuelles Dokument" />
								{errorsAlerts.length > 0 && (
									<div className="flex justify-end items-center">
										<IconButton onClick={handleClick}>
											<Badge
												badgeContent={
													errorsAlerts.length
												}
												color="warning"
											>
												<FeedbackIcon />
											</Badge>
										</IconButton>
										<Menu
											anchorEl={anchorEl}
											open={open}
											onClose={handleClose}
										>
											{errorsAlerts.map(
												(alert, index) => (
													<Alert
														severity={
															alert.severity
														}
														key={index}
													>
														{alert.message}
													</Alert>
												)
											)}
										</Menu>
									</div>
								)}
							</Tabs>
						</div>
						<CustomTabPanel value={currentTab} index={0}>
							<>
								<BankAccountSelect
									onChange={(value) =>
										setSelectedBankAccount(
											bankAccountsLookup[value as string]
										)
									}
									value={bankAccount?.id.toString()}
								/>
								<ClientSelect
									value={client?.id?.toString()}
									onChange={(value) =>
										setSelectedClient(
											clientsLookup[value as string]
										)
									}
								/>
							</>
						</CustomTabPanel>
						<CustomTabPanel value={currentTab} index={1}>
							<Accordion
								type="single"
								collapsible
								className="w-full"
							>
								<PDFTemplateConfigurationSettings
									template={"general"}
								/>
								<AccordionItem value="item-6">
									<AccordionTrigger>Layout</AccordionTrigger>

									<AccordionContent>
										<CustomTextFieldWithSaveButton
											label="Rand oben in mm"
											value={
												pdfTemplateConfiguration
													?.general?.marginTop
											}
											onSave={(value) => {
												updatePdfTemplateConfiguration(
													"general",
													{
														marginTop:
															parseInt(value),
													}
												);
											}}
											validationError={null}
											fullWidth
										/>
										<CustomTextFieldWithSaveButton
											label="Rand links in mm"
											value={
												pdfTemplateConfiguration
													?.general?.marginLeft
											}
											onSave={(value) => {
												updatePdfTemplateConfiguration(
													"general",
													{
														marginLeft:
															parseInt(value),
													}
												);
											}}
											validationError={null}
											fullWidth
										/>
										<CustomTextFieldWithSaveButton
											label="Rand rechts in mm"
											value={
												pdfTemplateConfiguration
													?.general?.marginRight
											}
											onSave={(value) => {
												updatePdfTemplateConfiguration(
													"general",
													{
														marginRight:
															parseInt(value),
													}
												);
											}}
											validationError={null}
											fullWidth
										/>
										<CustomTextFieldWithSaveButton
											label="Rand unten in mm"
											value={
												pdfTemplateConfiguration
													?.general?.marginBottom
											}
											onSave={(value) => {
												updatePdfTemplateConfiguration(
													"general",
													{
														marginBottom:
															parseInt(value),
													}
												);
											}}
											validationError={null}
											fullWidth
										/>
										<div className="mt-4">
											<FormLabel>
												<div className="font-bold text-lg">
													Datumsformat
												</div>
											</FormLabel>
											<ToggleButtonGroup
												color="primary"
												value={
													pdfTemplateConfiguration
														?.general?.dateFormat ??
													"short"
												}
												exclusive
												onChange={(e, v) => {
													if (v !== null) {
														updatePdfTemplateConfiguration(
															"general",
															{
																dateFormat:
																	v as
																		| "short"
																		| "long",
															}
														);
													}
												}}
												aria-label="Datumsformat"
											>
												<ToggleButton value="short">
													7.7.2026
												</ToggleButton>
												<ToggleButton value="long">
													7. Juli 2026
												</ToggleButton>
											</ToggleButtonGroup>
										</div>
										<div>
											<FormControl>
												<FormLabel id="demo-radio-buttons-group-label">
													<div className="font-bold text-lg">
														Grösse
													</div>
												</FormLabel>
												<RadioGroup
													aria-labelledby="demo-radio-buttons-group-label"
													name="radio-buttons-group"
													value={
														pdfTemplateConfiguration
															?.general.size ??
														"lg"
													}
													sx={{
														display: "flex",
													}}
													onChange={(e, v) => {
														updatePdfTemplateConfiguration(
															"general",
															{
																size: v as
																	| "sm"
																	| "md"
																	| "lg",
															}
														);
													}}
												>
													<FormControlLabel
														value="sm"
														control={
															<Radio
																checked={
																	pdfTemplateConfiguration
																		?.general
																		.size ==
																	"sm"
																}
																size="small"
															/>
														}
														label="Klein"
													/>
													<FormControlLabel
														value="md"
														control={
															<Radio
																checked={
																	pdfTemplateConfiguration
																		?.general
																		.size ==
																	"md"
																}
															/>
														}
														label="Mittel"
													/>
													<FormControlLabel
														value="lg"
														control={
															<Radio
																checked={
																	pdfTemplateConfiguration
																		?.general
																		.size ==
																	"lg"
																}
																sx={{
																	"& .MuiSvgIcon-root":
																		{
																			fontSize: 30,
																		},
																}}
															/>
														}
														label="Gross"
													/>
												</RadioGroup>
											</FormControl>
										</div>
										<div className="mt-4">
											<FormControl>
												<FormLabel id="table-density-radio-buttons-group-label">
													<div className="font-bold text-lg">
														Dichte
													</div>
												</FormLabel>
												<RadioGroup
													aria-labelledby="table-density-radio-buttons-group-label"
													name="table-density-radio-buttons-group"
													value={
														pdfTemplateConfiguration
															?.general
															.tableDensity ??
														"md"
													}
													sx={{
														display: "flex",
													}}
													onChange={(e, v) => {
														updatePdfTemplateConfiguration(
															"general",
															{
																tableDensity:
																	v as
																		| "sm"
																		| "md"
																		| "lg",
															}
														);
													}}
												>
													<FormControlLabel
														value="sm"
														control={
															<Radio
																checked={
																	pdfTemplateConfiguration
																		?.general
																		.tableDensity ==
																	"sm"
																}
																size="small"
															/>
														}
														label="Klein"
													/>
													<FormControlLabel
														value="md"
														control={
															<Radio
																checked={
																	pdfTemplateConfiguration
																		?.general
																		.tableDensity ==
																	"md"
																}
															/>
														}
														label="Mittel"
													/>
													<FormControlLabel
														value="lg"
														control={
															<Radio
																checked={
																	pdfTemplateConfiguration
																		?.general
																		.tableDensity ==
																	"lg"
																}
																sx={{
																	"& .MuiSvgIcon-root":
																		{
																			fontSize: 30,
																		},
																}}
															/>
														}
														label="Gross"
													/>
												</RadioGroup>
											</FormControl>
										</div>
										<div className="mt-4">
											<label className="block text-sm font-medium text-gray-700 mb-2">
												Tabellenhintergrundfarbe
											</label>
											<div className="flex items-center gap-2">
												<ColorPicker
													currentColor={
														pdfTemplateConfiguration
															?.general
															.tableBackgroundColor
													}
													onColorSelect={(color) => {
														updatePdfTemplateConfiguration(
															"general",
															{
																tableBackgroundColor:
																	color ||
																	"#d3d3d3",
															}
														);
													}}
													defaultColor="#d3d3d3"
													showReset
													label="Tabellenhintergrundfarbe wählen"
												/>
												<span className="text-sm text-gray-600">
													{pdfTemplateConfiguration
														?.general
														.tableBackgroundColor ===
													"#d3d3d3"
														? "Standard (Hellgrau)"
														: pdfTemplateConfiguration?.general.tableBackgroundColor?.toUpperCase() ??
															"Standard (Hellgrau)"}
												</span>
											</div>
										</div>
										<div className="mt-4">
											<label className="block text-sm font-medium text-gray-700 mb-2">
												Hintergrundbild
											</label>
											<FormControlLabel
												control={
													<Checkbox
														checked={
															pdfTemplateConfiguration
																?.general
																?.backgroundImageEnabled ??
															false
														}
														onChange={(e) => {
															updatePdfTemplateConfiguration(
																"general",
																{
																	backgroundImageEnabled:
																		e.target
																			.checked,
																}
															);
														}}
													/>
												}
												label="Hintergrundbild aktivieren"
											/>
											{pdfTemplateConfiguration?.general
												?.backgroundImageEnabled && (
												<div className="flex flex-col gap-2 mt-2">
													<label
														htmlFor="background-image-upload"
														className="cursor-pointer inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 w-fit"
														data-testid="background-image-upload-button"
														aria-label="Hintergrundbild hochladen"
													>
														{pdfTemplateConfiguration
															?.general
															.backgroundImageUrl
															? "Bild ändern"
															: "Bild hochladen"}
														<input
															type="file"
															className="hidden"
															id="background-image-upload"
															accept=".png,.jpg,.jpeg,.pdf"
															data-testid="background-image-upload-input"
															onChange={async (
																e
															) => {
																const file =
																	e.target
																		.files?.[0];
																if (!file)
																	return;
																if (
																	file.size >
																	5 *
																		1024 *
																		1024
																) {
																	showNotification(
																		{
																			message:
																				"Bitte wählen Sie eine Datei kleiner als 5MB",
																			type: "error",
																		}
																	);
																	return;
																}

																let uploadFile:
																	| File
																	| Blob =
																	file;
																let fileName =
																	file.name;

																if (
																	file.type ===
																	"application/pdf"
																) {
																	try {
																		const pages =
																			await renderPdfToCanvases(
																				file,
																				3
																			);
																		if (
																			!pages.length
																		) {
																			showNotification(
																				{
																					message:
																						"PDF konnte nicht gelesen werden",
																					type: "error",
																				}
																			);
																			return;
																		}
																		const canvas =
																			pages[0]
																				.canvas;
																		const blob: Blob | null =
																			await new Promise(
																				(
																					resolve
																				) =>
																					canvas.toBlob(
																						resolve,
																						"image/png"
																					)
																			);
																		if (
																			!blob
																		) {
																			showNotification(
																				{
																					message:
																						"PDF-Konvertierung fehlgeschlagen",
																					type: "error",
																				}
																			);
																			return;
																		}
																		uploadFile =
																			blob;
																		fileName =
																			file.name.replace(
																				/\.pdf$/i,
																				".png"
																			);
																	} catch {
																		showNotification(
																			{
																				message:
																					"PDF konnte nicht konvertiert werden",
																				type: "error",
																			}
																		);
																		return;
																	}
																}

																const path = `${organization?.id}/backgrounds/${uuidv4()}/${fileName}`;
																const {
																	success,
																	url,
																} =
																	await uploadFileWithPublicUrl(
																		path,
																		uploadFile
																	);
																if (
																	success &&
																	url
																) {
																	updatePdfTemplateConfiguration(
																		"general",
																		{
																			backgroundImageUrl:
																				url,
																			backgroundImageEnabled:
																				true,
																		}
																	);
																}
															}}
														/>
													</label>
													{pdfTemplateConfiguration
														?.general
														.backgroundImageUrl && (
														<>
															<div className="flex items-center gap-3">
																<img
																	src={
																		pdfTemplateConfiguration
																			.general
																			.backgroundImageUrl
																	}
																	alt="Hintergrundbild Vorschau"
																	className="h-16 border border-gray-200 rounded object-cover"
																	data-testid="background-image-preview"
																/>
																<button
																	className="text-sm text-red-600 hover:text-red-800"
																	data-testid="background-image-remove-button"
																	aria-label="Hintergrundbild entfernen"
																	onClick={() => {
																		updatePdfTemplateConfiguration(
																			"general",
																			{
																				backgroundImageUrl:
																					"",
																				backgroundImageEnabled:
																					false,
																			}
																		);
																	}}
																>
																	Entfernen
																</button>
															</div>
															<FormControlLabel
																control={
																	<Checkbox
																		checked={
																			pdfTemplateConfiguration
																				?.general
																				?.backgroundImageFirstPageOnly ??
																			false
																		}
																		onChange={(
																			e
																		) => {
																			updatePdfTemplateConfiguration(
																				"general",
																				{
																					backgroundImageFirstPageOnly:
																						e
																							.target
																							.checked,
																				}
																			);
																		}}
																	/>
																}
																label="Nur auf erster Seite"
															/>
														</>
													)}
												</div>
											)}
										</div>
									</AccordionContent>
								</AccordionItem>
								<AccordionItem value="item-formatting">
									<AccordionTrigger>
										Formatierung
									</AccordionTrigger>
									<AccordionContent>
										<div className="flex flex-col gap-2 mt-4 pb-4">
											<FormControlLabel
												control={
													<Checkbox
														checked={
															pdfTemplateConfiguration
																?.general
																?.isTotalBold ??
															false
														}
														onChange={(e) => {
															updatePdfTemplateConfiguration(
																"general",
																{
																	isTotalBold:
																		e.target
																			.checked,
																}
															);
														}}
													/>
												}
												label="Total fettgedruckt"
											/>
											<FormControlLabel
												control={
													<Checkbox
														checked={
															pdfTemplateConfiguration
																?.general
																?.isPatientBold ??
															false
														}
														onChange={(e) => {
															updatePdfTemplateConfiguration(
																"general",
																{
																	isPatientBold:
																		e.target
																			.checked,
																}
															);
														}}
													/>
												}
												label="Patient fettgedruckt"
											/>
											<FormControlLabel
												control={
													<Checkbox
														checked={
															pdfTemplateConfiguration
																?.general
																?.isPractitionerBold ??
															false
														}
														onChange={(e) => {
															updatePdfTemplateConfiguration(
																"general",
																{
																	isPractitionerBold:
																		e.target
																			.checked,
																}
															);
														}}
													/>
												}
												label="Behandler fettgedruckt"
											/>
											<FormControlLabel
												control={
													<Checkbox
														checked={
															pdfTemplateConfiguration
																?.general
																?.showZeroVat ??
															false
														}
														onChange={(e) => {
															updatePdfTemplateConfiguration(
																"general",
																{
																	showZeroVat:
																		e.target
																			.checked,
																}
															);
														}}
													/>
												}
												label="MwSt. immer in Zusammenfassung anzeigen"
											/>
											<FormControlLabel
												control={
													<Checkbox
														checked={
															pdfTemplateConfiguration
																?.general
																?.showTotalCurrency ??
															true
														}
														onChange={(e) => {
															updatePdfTemplateConfiguration(
																"general",
																{
																	showTotalCurrency:
																		e.target
																			.checked,
																}
															);
														}}
													/>
												}
												label="Währung bei Total anzeigen"
											/>
											<FormControlLabel
												control={
													<Checkbox
														checked={
															pdfTemplateConfiguration
																?.general
																?.qrSlipOnExtraPage ??
															false
														}
														onChange={(e) => {
															updatePdfTemplateConfiguration(
																"general",
																{
																	qrSlipOnExtraPage:
																		e.target
																			.checked,
																}
															);
														}}
													/>
												}
												label="QR-Einzahlungsschein immer auf separater Seite"
											/>
										</div>
									</AccordionContent>
								</AccordionItem>
								<AccordionItem value="item-5">
									<AccordionTrigger>
										Firmenlogo
									</AccordionTrigger>
									<AccordionContent>
										<PdfTemplateLogo
											pdfTemplateConfiguration={
												pdfTemplateConfiguration
											}
										/>
									</AccordionContent>
								</AccordionItem>
							</Accordion>
						</CustomTabPanel>
						<CustomTabPanel value={currentTab} index={2}>
							{selectedPdfTemplate ===
							PdfTemplateType.ETIK_AUFTRAG ? (
								<PdfTemplateSingleConfigurations
									pdfTemplateType={selectedPdfTemplate}
								/>
							) : (
								<>
									<FormControlLabel
										className="mb-1"
										control={
											<Checkbox
												disabled={
													!pdfTemplateConfiguration
														?.general.logoImgurl
												}
												checked={
													pdfTemplateConfiguration?.[
														selectedPdfTemplate
													].showLogo
												}
												onChange={(e) => {
													updatePdfTemplateConfiguration(
														selectedPdfTemplate,
														{
															showLogo:
																e.target
																	.checked,
														}
													);
												}}
											/>
										}
										label="Logos anzeigen"
									/>
									<FormControlLabel
										className="mb-1"
										control={
											<Checkbox
												disabled={
													!pdfTemplateConfiguration
														?.general
														.customFooterEnabled
												}
												checked={
													pdfTemplateConfiguration?.[
														selectedPdfTemplate
													].customFooterEnabled
												}
												onChange={(e) => {
													updatePdfTemplateConfiguration(
														selectedPdfTemplate,
														{
															customFooterEnabled:
																e.target
																	.checked,
														}
													);
												}}
											/>
										}
										label="Individuelle Fusszeile anzeigen"
									/>
									<FormControlLabel
										className="mb-1"
										control={
											<Checkbox
												disabled={
													!pdfTemplateConfiguration
														?.general
														.backgroundImageEnabled ||
													!pdfTemplateConfiguration
														?.general
														.backgroundImageUrl
												}
												checked={
													pdfTemplateConfiguration?.[
														selectedPdfTemplate
													].backgroundImageEnabled ??
													true
												}
												onChange={(e) => {
													updatePdfTemplateConfiguration(
														selectedPdfTemplate,
														{
															backgroundImageEnabled:
																e.target
																	.checked,
														}
													);
												}}
											/>
										}
										label="Hintergrundbild anzeigen"
									/>
									{pdfTemplateConfiguration?.[
										selectedPdfTemplate
									].backgroundImageEnabled !== false &&
										pdfTemplateConfiguration?.general
											.backgroundImageEnabled &&
										pdfTemplateConfiguration?.general
											.backgroundImageUrl && (
											<FormControlLabel
												className="mb-1 ml-4"
												control={
													<Checkbox
														checked={
															pdfTemplateConfiguration?.[
																selectedPdfTemplate
															]
																.backgroundImageFirstPageOnly ??
															pdfTemplateConfiguration
																?.general
																?.backgroundImageFirstPageOnly ??
															false
														}
														onChange={(e) => {
															updatePdfTemplateConfiguration(
																selectedPdfTemplate,
																{
																	backgroundImageFirstPageOnly:
																		e.target
																			.checked,
																}
															);
														}}
													/>
												}
												label="Nur auf erster Seite"
											/>
										)}
									<Accordion
										type="single"
										collapsible
										className="w-full"
									>
										<PDFTemplateConfigurationSettings
											template={selectedPdfTemplate}
										/>
										{selectedPdfTemplate ===
											PdfTemplateType.SAMMELRECHNUNG && (
											<AccordionItem value="item-beschriftung">
												<AccordionTrigger>
													Beschriftung
												</AccordionTrigger>
												<AccordionContent>
													<TitleDateModeSettings
														pdfTemplateConfiguration={
															pdfTemplateConfiguration
														}
														updatePdfTemplateConfiguration={
															updatePdfTemplateConfiguration
														}
														resetConfigurationColumn={
															resetConfigurationColumn
														}
													/>
												</AccordionContent>
											</AccordionItem>
										)}
										{isReminderTemplateType(
											selectedPdfTemplate
										) && (
											<AccordionItem value="item-reminder-letter">
												<AccordionTrigger
													aria-label="Titel und Brieftext"
													data-testid="reminder-letter-accordion"
												>
													Titel und Brieftext
												</AccordionTrigger>
												<AccordionContent>
													<ReminderLetterSettings
														key={
															selectedPdfTemplate
														}
														pdfTemplateType={
															selectedPdfTemplate
														}
													/>
												</AccordionContent>
											</AccordionItem>
										)}
										<AccordionItem value="item-7">
											<AccordionTrigger id="Footer alignment">
												Fusszeile
											</AccordionTrigger>
											<AccordionContent>
												<div className="flex flex-col gap-2">
													<ToggleButtonGroup
														color="primary"
														value={
															pdfTemplateConfiguration
																?.general
																.footerAlignment
														}
														exclusive
														className="mb-4"
														onChange={(e, v) => {
															updatePdfTemplateConfiguration(
																"general",
																{
																	footerAlignment:
																		v as Alignment,
																}
															);
														}}
														aria-label="Platform"
													>
														<ToggleButton value="left">
															Links
														</ToggleButton>
														<ToggleButton value="center">
															Zentriert
														</ToggleButton>
														<ToggleButton value="right">
															Rechts
														</ToggleButton>
													</ToggleButtonGroup>
												</div>

												<PdfTemplateSingleConfigurations
													pdfTemplateType={
														selectedPdfTemplate
													}
												/>
											</AccordionContent>
										</AccordionItem>
										{[
											PdfTemplateType.LIEFERSCHEIN,
											PdfTemplateType.KOSTENVORANSCHLAG,
											PdfTemplateType.RECHNUNG,
											PdfTemplateType.GUTSCHRIFT,
											PdfTemplateType.AUFTRAGSBLATT,
										].includes(selectedPdfTemplate) && (
											<AccordionItem value="item-article-fields">
												<AccordionTrigger>
													Artikelfelder in Tabelle
												</AccordionTrigger>
												<AccordionContent>
													<div className="flex flex-col gap-1">
														<FormControlLabel
															control={
																<Checkbox
																	checked={
																		(
																			pdfTemplateConfiguration?.[
																				selectedPdfTemplate
																			] as any
																		)
																			?.articleFields
																			?.showAdditionalText ??
																		false
																	}
																	onChange={(
																		e
																	) => {
																		updatePdfTemplateConfiguration(
																			selectedPdfTemplate,
																			{
																				articleFields:
																					{
																						...(
																							pdfTemplateConfiguration?.[
																								selectedPdfTemplate
																							] as any
																						)
																							?.articleFields,
																						showAdditionalText:
																							e
																								.target
																								.checked,
																					},
																			}
																		);
																	}}
																/>
															}
															label="Zusatztext"
														/>
														<FormControlLabel
															control={
																<Checkbox
																	checked={
																		(
																			pdfTemplateConfiguration?.[
																				selectedPdfTemplate
																			] as any
																		)
																			?.articleFields
																			?.showManufacturer ??
																		false
																	}
																	onChange={(
																		e
																	) => {
																		updatePdfTemplateConfiguration(
																			selectedPdfTemplate,
																			{
																				articleFields:
																					{
																						...(
																							pdfTemplateConfiguration?.[
																								selectedPdfTemplate
																							] as any
																						)
																							?.articleFields,
																						showManufacturer:
																							e
																								.target
																								.checked,
																					},
																			}
																		);
																	}}
																/>
															}
															label="Hersteller"
														/>
														<FormControlLabel
															control={
																<Checkbox
																	checked={
																		(
																			pdfTemplateConfiguration?.[
																				selectedPdfTemplate
																			] as any
																		)
																			?.articleFields
																			?.showCeNumber ??
																		false
																	}
																	onChange={(
																		e
																	) => {
																		updatePdfTemplateConfiguration(
																			selectedPdfTemplate,
																			{
																				articleFields:
																					{
																						...(
																							pdfTemplateConfiguration?.[
																								selectedPdfTemplate
																							] as any
																						)
																							?.articleFields,
																						showCeNumber:
																							e
																								.target
																								.checked,
																					},
																			}
																		);
																	}}
																/>
															}
															label="CE-Nummer"
														/>
														<FormControlLabel
															control={
																<Checkbox
																	checked={
																		(
																			pdfTemplateConfiguration?.[
																				selectedPdfTemplate
																			] as any
																		)
																			?.articleFields
																			?.showComposition ??
																		false
																	}
																	onChange={(
																		e
																	) => {
																		updatePdfTemplateConfiguration(
																			selectedPdfTemplate,
																			{
																				articleFields:
																					{
																						...(
																							pdfTemplateConfiguration?.[
																								selectedPdfTemplate
																							] as any
																						)
																							?.articleFields,
																						showComposition:
																							e
																								.target
																								.checked,
																					},
																			}
																		);
																	}}
																/>
															}
															label="Zusammensetzung"
														/>
														<FormControlLabel
															control={
																<Checkbox
																	checked={
																		(
																			pdfTemplateConfiguration?.[
																				selectedPdfTemplate
																			] as any
																		)
																			?.articleFields
																			?.showLotNumber ??
																		false
																	}
																	onChange={(
																		e
																	) => {
																		updatePdfTemplateConfiguration(
																			selectedPdfTemplate,
																			{
																				articleFields:
																					{
																						...(
																							pdfTemplateConfiguration?.[
																								selectedPdfTemplate
																							] as any
																						)
																							?.articleFields,
																						showLotNumber:
																							e
																								.target
																								.checked,
																					},
																			}
																		);
																	}}
																/>
															}
															label="Lot-Nummer"
														/>
													</div>
												</AccordionContent>
											</AccordionItem>
										)}
									</Accordion>
								</>
							)}
						</CustomTabPanel>
					</div>
				</div>
				<div
					style={{
						height: "100%",
						padding: "40px",
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
					}}
				>
					<div
						style={{
							boxShadow: "0 5px 10px 0 rgba(0, 0, 0, 0.3)",
							position: "relative",
						}}
					>
						<Document
							onLoadSuccess={(n) => {
								setNumPages(n._pdfInfo?.numPages);
								handleDocumentHasFinishedLoading();
							}}
							file={`data:application/pdf;base64,${pdfBase64String}`}
							loading={<PdfPreviewLoadingPlaceholder />}
							error={"PDF konnte nicht geladen werden"}
						>
							<Page
								onLoadSuccess={handlePageHasFinishedLoading}
								pageNumber={pageNumber}
								width={500}
								loading={<PdfPreviewLoadingPlaceholder />}
								error={"Seite konnte nicht geladen werden"}
							/>
						</Document>
						<PdfPreviewLoadingOverlay
							isLoading={isDocumentLoading || isPageLoading}
						/>
					</div>
					<div
						style={{
							marginTop: 24,
							display: "flex",
							justifyContent: "center",
							width: "full",
							alignItems: "center",
						}}
					>
						<IconButton
							disabled={pageNumber <= 1}
							onClick={goBackPage}
						>
							<IconChevronLeft />
						</IconButton>
						{pageNumber}
						<IconButton
							disabled={pageNumber >= numPages}
							onClick={goForwardPage}
						>
							<IconChevronRight />
						</IconButton>
					</div>
				</div>
				<div
					style={{
						position: "relative",
					}}
				>
					<div
						style={{
							position: "absolute",
							left: "-10000px",
							top: "-10000px",
						}}
						key={previewKey}
					>
						<div className="pdf-preview" key={previewKey}>
							<PdfTemplateOptions
								client={client}
								jobExample={jobExample}
								jobItems={jobItemExamples}
								jobDocumentExample={
									jobDocumentExample[selectedPdfTemplate]
								}
								organization={organization}
								pdfTemplateConfiguration={
									pdfTemplateConfiguration
								}
								patientExample={patientExample}
								practitionerExample={practitionerExample}
								jobTotals={jobTotals}
								paymentSlipData={paymentSlipData}
								sammelrechnungCalculationResult={
									sammelrechnungCalculationResult
								}
								selectedPdfTemplate={selectedPdfTemplate}
								clientAvatarUrl={clientAvatarUrl ?? undefined}
								currencyConverterValues={previewCurrencyValues}
							/>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

const PdfPreviewLoadingPlaceholder: React.FC = () => {
	return (
		<div
			style={{
				top: 0,
				zIndex: 101,
				backgroundColor: "white",
				width: 500,
				height: (500 / 210) * 297,
			}}
		></div>
	);
};

/** We use this in combination with PdfPreviewLoadingPlaceholder to override the loading feedback react-pdf provides */
const PdfPreviewLoadingOverlay: React.FC<{
	isLoading: boolean;
}> = ({ isLoading }) => {
	return (
		<div
			style={{
				position: "absolute",
				top: 0,
				left: 0,
				width: "100%",
				height: "100%",
				backgroundColor: "white",
				display: "flex",
				justifyContent: "center",
				alignItems: "center",
				zIndex: isLoading ? 101 : -1,
			}}
		>
			<CircularProgress />
		</div>
	);
};

interface TabPanelProps {
	children?: React.ReactNode;
	index: number;
	value: number;
}

function CustomTabPanel(props: TabPanelProps) {
	const { children, value, index, ...other } = props;

	return (
		<div
			role="tabpanel"
			hidden={value !== index}
			id={`simple-tabpanel-${index}`}
			aria-labelledby={`simple-tab-${index}`}
			className="overflow-y-auto"
			{...other}
		>
			{value === index && <div style={{ padding: 3 }}>{children}</div>}
		</div>
	);
}
